/**
 * O vocabulário da base de conhecimento, do lado da tela.
 *
 * Espelha `apps/api/src/itsm/knowledge/` sem importar de lá: o front e a API
 * são compilados separados, e um `import` atravessando a fronteira acoplaria a
 * tela ao Prisma. O que garante que os dois não divirjam é o teste de
 * arquitetura mais a revisão — não um tipo compartilhado que ninguém verifica.
 */

export const SITUACOES_DE_ARTIGO = [
  'DRAFT',
  'IN_REVIEW',
  'APPROVED',
  'PUBLISHED',
  'ARCHIVED',
] as const;
export type SituacaoDeArtigo = (typeof SITUACOES_DE_ARTIGO)[number];

export const ROTULO_DE_SITUACAO: Record<SituacaoDeArtigo, string> = {
  DRAFT: 'Rascunho',
  IN_REVIEW: 'Em revisão',
  APPROVED: 'Aprovado',
  PUBLISHED: 'Publicado',
  ARCHIVED: 'Arquivado',
};

/**
 * O que cada situação significa para quem lê o artigo — e não o que ela é.
 *
 * "Aprovado" é o que mais se erra: quem vê a palavra supõe que está no ar.
 */
export const EFEITO_DE_SITUACAO: Record<SituacaoDeArtigo, string> = {
  DRAFT: 'Só quem opera vê. Não aparece na busca nem na sugestão dentro do chamado.',
  IN_REVIEW: 'Alguém está conferindo. Se já estava publicado, continua no ar.',
  APPROVED: 'Conferido, mas ainda NÃO está no ar — falta publicar.',
  PUBLISHED: 'No ar: entra na busca e é oferecido durante o atendimento.',
  ARCHIVED: 'Fora da busca. O texto e o histórico continuam consultáveis.',
};

export const VISIBILIDADES = ['INTERNAL', 'CUSTOMER', 'PUBLIC'] as const;
export type Visibilidade = (typeof VISIBILIDADES)[number];

export const ROTULO_DE_VISIBILIDADE: Record<Visibilidade, string> = {
  INTERNAL: 'Interno',
  CUSTOMER: 'Cliente',
  PUBLIC: 'Público',
};

/**
 * Quem alcança o artigo em cada visibilidade.
 *
 * O texto é deliberadamente concreto. Escolher "Cliente" num procedimento que
 * cita senha de firewall é o erro que esta fase mais precisa evitar, e ele se
 * evita dizendo o que a escolha FAZ, não repetindo o nome dela.
 */
export const ALCANCE_DE_VISIBILIDADE: Record<Visibilidade, string> = {
  INTERNAL: 'Só quem trabalha na Aptum. Nunca sai pelo portal do cliente.',
  CUSTOMER: 'Os clientes com acesso ao portal, depois de publicado.',
  PUBLIC: 'Qualquer pessoa, inclusive fora do portal.',
};

export interface ArtigoListado {
  id: string;
  code: string;
  title: string;
  summary: string;
  status: SituacaoDeArtigo;
  visibility: Visibilidade;
  categoryId: string | null;
  ownerId: string | null;
  authorId: string | null;
  nextReviewAt: string | null;
  lastReviewedAt: string | null;
  currentVersion: number;
  usageCount: number;
  resolvedCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  updatedAt: string;
  category?: { id: string; name: string; color: string | null } | null;
}

export interface VersaoDeArtigo {
  id: string;
  number: number;
  title: string;
  summary: string;
  bodyHtml: string;
  keywords: string[];
  status: SituacaoDeArtigo;
  visibility: Visibilidade;
  changeNote: string | null;
  restoredFrom: number | null;
  authorId: string | null;
  createdAt: string;
}

export interface ChamadoResumido {
  id: string;
  number: string;
  subject: string;
  openedAt?: string;
}

export interface Artigo extends ArtigoListado {
  bodyHtml: string;
  keywords: string[];
  serviceIds: string[];
  ciIds: string[];
  reviewIntervalDays: number;
  approvedAt: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  version: number;
  sourceTicketId: string | null;
  sourceTicket?: ChamadoResumido | null;
  knownErrors: Array<{ id: string; code: string; title: string; status: string }>;
  versions: VersaoDeArtigo[];
  ticketLinks: Array<{
    ticketId: string;
    resolved: boolean;
    helpful: boolean | null;
    note: string | null;
    usedAt: string;
    ticket: ChamadoResumido;
  }>;
}

/** Uma linha do resultado de busca. */
export interface ArtigoEncontrado {
  id: string;
  code: string;
  title: string;
  summary: string;
  status: SituacaoDeArtigo;
  visibility: Visibilidade;
  categoryId: string | null;
  usageCount: number;
  resolvedCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  nextReviewAt: string | null;
  escore: number;
  termos: number;
}

export interface SugestaoDeArtigo extends ArtigoEncontrado {
  motivos: string[];
  jaVinculado: boolean;
}

export interface Transicao {
  de: SituacaoDeArtigo;
  para: SituacaoDeArtigo;
  permissao: string;
  rotulo: string;
  explicacao: string;
}

/**
 * A revisão venceu?
 *
 * A conta é da tela de propósito: o servidor devolve a data, e transformá-la em
 * "vencido" no back-end congelaria o resultado no instante da resposta. Uma
 * página aberta às 23h59 diria "em dia" durante toda a madrugada.
 */
export function revisaoVencida(nextReviewAt: string | null, agora = new Date()): boolean {
  return nextReviewAt !== null && new Date(nextReviewAt).getTime() <= agora.getTime();
}

/** Quantos dias faltam para a revisão. Negativo quando já venceu. */
export function diasAteRevisao(nextReviewAt: string | null, agora = new Date()): number | null {
  if (nextReviewAt === null) return null;
  const ms = new Date(nextReviewAt).getTime() - agora.getTime();
  return Math.ceil(ms / 86_400_000);
}
